import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { cachedLists } from 'src/stores/device-runtime/firmware-ui';
import { devices } from 'src/stores/lists/devices';
import { get } from 'svelte/store';

import type { Device } from 'src/types/Device';
import {
	getBleMacFromDevice,
	isWallDisplay,
	getBluetoothMacFromId,
	is_gen_2,
} from './interface_helpers';
import { SLEEPING_TYPES } from 'src/constants';

import { gen2Request } from 'src/services/gen_2_http_reqs';

import type { SettingRequest } from 'src/types/requests/settings/setting';
import { compareFWVersions } from './firmware_compare_helpers';
import { secureModeFirmwareVersions } from './constants';
import { compare } from 'compare-versions';
import {
	buildBleSetConfig,
	firmwareOmitsBleEnable,
	formatBleMacAddress,
	isBleGatewayFwAlwaysOn,
	isGatewayObserverEnabledFromSettings,
	pickBleMacFromSources,
} from './ble_pairing_helpers';

import type { BleMacSources } from './ble_pairing_helpers';

export {
	parseBleFwVersion,
	isBleFwAtLeast,
	hasBlePairingApi,
	isBleObserverAutomatic,
	stripBleAddrTypeSuffix,
	formatBleMacAddress,
	pickBleMacFromSources,
} from './ble_pairing_helpers';

/**
 * Checks if the device firmware version is >= 2.0.0 based on settings.
 * In FW 2.0.0+ the global `ble.enable` flag is removed — BLE auto-activates.
 * Only `ble.rpc.enable` remains to control BLE RPC access, and the new
 * BLE.StartPairing / StopPairing / ListPairedDevices / DeletePairedDevice
 * methods and `flags` / `pairing` status fields are introduced.
 */
export const isFwAfter200 = (settings: any): boolean => {
	const fwVersion = (
		settings?.DeviceInfo?.ver ||
		settings?.sys?.device?.fw_id ||
		settings?.DeviceInfo?.fw_id
	)?.match(/(\d{1,}\.){1,}\d{1,}/)?.[0];

	if (!fwVersion) return false;

	try {
		return compare(fwVersion, secureModeFirmwareVersions, '>=');
	} catch {
		return false;
	}
};

export const isGatewayObserver = (device: Device | string) => {
	const device_id = typeof device === 'string' ? device : device.id;
	const settings = devices_settings.getByRealId(device_id);
	const status = devices_status.getByRealId(device_id);

	if (isBleGatewayFwAlwaysOn(settings)) return true;

	return (
		typeof settings?.ble?.observer !== 'undefined' || typeof status?.sys?.btrelay_rev !== 'undefined'
	);
};

export const isGatewayObserverEnabled = (device: Device | string) => {
	const device_id = typeof device === 'string' ? device : device.id;
	const settings = devices_settings.getByRealId(device_id);
	const status = devices_status.getByRealId(device_id);

	return isGatewayObserverEnabledFromSettings(settings, status);
};

/**
 * Filter the devices to only those with enabled observers
 * 	 Checks the provided device ids to return those with enabled observers
 *   Note: Logic is the same as isGatewayObserverEnabled, but handles list
 *
 * @param devices  Array of device ids
 * @returns   The Array of device ids, which have enabled observer
 */
export const filterEnabledObserverIds = (devices: string[]): string[] => {
	if (!devices || devices.length === 0) return [];

	// Find only the devices with enabled observer
	return devices.filter((device_id) => {
		const settings = devices_settings.getByRealId(device_id);
		const status = devices_status.getByRealId(device_id);

		if (isBleGatewayFwAlwaysOn(settings)) return true;

		return (
			typeof status?.sys?.btrelay_rev !== 'undefined' ||
			(typeof settings?.ble?.observer !== 'undefined' &&
				(settings?.ble?.observer?.always_on || settings?.ble?.observer?.enable))
		);
	});
};

/** Find all GEN 2 devices set up as observers (filter only main id for multi channel devices) */
export const getAvaliableBleGateways = () => {
	let _devices = get(devices);

	return Object.values(_devices).filter((dev: Device) => {
		const devSettings = devices_settings.getByRealId(dev.id);
		const devStatus = devices_status.getByRealId(dev.id);

		let isObserverCheck = isBleGatewayFwAlwaysOn(devSettings)
			? true
			: typeof devSettings?.ble?.observer !== 'undefined' ||
				typeof devStatus?.sys?.btrelay_rev !== 'undefined';

		// Check if BLU Gateway Gen 3 fw version is lower than 1.5.0
		// then change the isObserverCheck
		if (
			dev.type === 'S3GW-1DBT001' &&
			compareFWVersions(devSettings?.DeviceInfo?.ver, '<', '1.5.0')
		) {
			isObserverCheck = devSettings?.ble?.enable;
		}

		return (
			is_gen_2(dev) &&
			!dev.shared &&
			isObserverCheck &&
			dev.channel == 0 &&
			devSettings?.cloud?.enable && // Only cloud enabled devices can be observers
			!SLEEPING_TYPES.includes(dev.type)
		);
	});
};

export function getAssociatedBleGatewayIdsForBluDevice(device: Device): string[] {
	if (device.gen !== 'GBLE' || device.shared) return [];

	const mac = getBluetoothMacFromId(device.id);
	const _cachedLists = get(cachedLists);

	return getAvaliableBleGateways()
		.filter((gatewayDevice) =>
			_cachedLists?.[gatewayDevice.id]?.['ble.cloudrelay.list']?.addrs?.includes(mac),
		)
		.map((gatewayDevice) => gatewayDevice.id);
}

export function getAssociatedBleGatewaysForBluDevices(
	bluDevices: Device[],
	excludeDeviceIds: string[] = [],
): Device[] {
	const excludeSet = new Set(excludeDeviceIds);
	const gatewayById = new Map(getAvaliableBleGateways().map((gateway) => [gateway.id, gateway]));
	const result = new Map<string, Device>();

	bluDevices.forEach((device) => {
		getAssociatedBleGatewayIdsForBluDevice(device).forEach((id) => {
			if (!excludeSet.has(id) && gatewayById.has(id)) {
				result.set(id, gatewayById.get(id)!);
			}
		});
	});

	return Array.from(result.values());
}

/** True when the gateway monitors BLU devices that are not part of the current transfer batch. */
export function gatewayMonitorsOtherBluDevices(
	gatewayId: string,
	transferringBluDeviceIds: string[],
): boolean {
	const transferringSet = new Set(transferringBluDeviceIds);
	const addrs = get(cachedLists)?.[gatewayId]?.['ble.cloudrelay.list']?.addrs ?? [];
	if (addrs.length === 0) return false;

	return Object.values(get(devices)).some((device: Device) => {
		if (device.gen !== 'GBLE' || device.shared || transferringSet.has(device.id)) {
			return false;
		}
		return addrs.includes(getBluetoothMacFromId(device.id));
	});
}

/** Wall Display fw from which inclusion window auto-disables Bluetooth (no app RPC needed). */
export const WALL_DISPLAY_AUTO_BLE_DISABLE_FW = '2.7.1';

/**
 * Wall Display fw < 2.7.1 does not auto-disable Bluetooth after inclusion —
 * the app must send Ble.SetConfig. Other devices / WD ≥ 2.7.1 handle it in FW.
 * Missing/unparseable ver is treated as needing the RPC (legacy WD safe default).
 */
export const needsPostInclusionBleDisableRpc = (deviceInfo: {
	model?: string;
	ver?: string;
}): boolean => {
	if (!isWallDisplay(deviceInfo?.model ?? '')) return false;
	if (!deviceInfo?.ver) return true;
	return !compareFWVersions(deviceInfo.ver, '>=', WALL_DISPLAY_AUTO_BLE_DISABLE_FW);
};

/**
 * Resolves the BLE MAC for display (uppercase, colon-separated).
 * Priority: status `addr` → status `mac` → RPC `addr` → RPC `mac` → derived
 * (non–Wall Display only). `addr` may include a type suffix (`,p`, `,rs`, …).
 */
export const resolveDisplayBleMac = (
	device: { id?: string; type?: string; mac?: string },
	sources: BleMacSources,
): string => {
	const fromStatus = pickBleMacFromSources(sources);
	if (fromStatus) return fromStatus;
	if (isWallDisplay(device.type ?? '')) return '';
	return formatBleMacAddress(getBleMacFromDevice(device));
};

/**
 * BLE MAC for the native Android bridge (lowercase 12-char hex, no separators).
 */
export const resolveBridgeBleMac = (
	device: { id?: string; type?: string; mac?: string },
	sources: BleMacSources,
): string => {
	const displayMac = resolveDisplayBleMac(device, sources);
	return displayMac ? displayMac.replace(/:/g, '').toLowerCase() : '';
};

const BTHOME_COMPONENT_REGEX = /^bthome(device|sensor|control):2\d{2}$/;
const BTHOME_CONTROL_KEY_PREFIX = 'bthomecontrol:';

const normalizeBleAddr = (addr: string): string => addr.replace(/[^a-fA-F0-9]/g, '').toLowerCase();

const matchesBleAddress = (component: unknown, addr: string): boolean => {
	if (!component || typeof component !== 'object') {
		return false;
	}

	const componentAddr =
		(component as Record<string, any>).addr || (component as Record<string, any>)._attrs?.addr;

	if (typeof componentAddr !== 'string') {
		return false;
	}

	return normalizeBleAddr(componentAddr) === normalizeBleAddr(addr);
};

const collectBTHomeKeysForAddr = (
	componentMap: Record<string, any> | undefined,
	addr: string,
): string[] =>
	Object.entries(componentMap || {})
		.filter(
			([key, component]) => BTHOME_COMPONENT_REGEX.test(key) && matchesBleAddress(component, addr),
		)
		.map(([key]) => key);

/**
 * Removes cached BTHome components for a BLE address from host status/settings.
 * Unpairing does not refresh those stores, so without this the UI keeps stale pairings.
 *
 * @returns Removed component keys (empty when nothing matched).
 */
export const removeCachedBTHomeComponents = (deviceID: string, addr: string): string[] => {
	if (!deviceID || !addr) {
		return [];
	}

	const settings = get(devices_settings)?.[deviceID] as Record<string, any> | undefined;
	const status = get(devices_status)?.[deviceID] as Record<string, any> | undefined;
	const removedKeys = [
		...new Set([
			...collectBTHomeKeysForAddr(settings, addr),
			...collectBTHomeKeysForAddr(status, addr),
		]),
	];

	if (!removedKeys.length) {
		return [];
	}

	if (settings) {
		const nextSettings = { ...settings };
		removedKeys.forEach((key) => delete nextSettings[key]);
		devices_settings.update_devices_settings(deviceID, nextSettings);
	}

	if (status) {
		const nextStatus = { ...status };
		removedKeys.forEach((key) => delete nextStatus[key]);
		devices_status.update_device_status({ id: deviceID, msg: nextStatus });
	}

	return removedKeys;
};

export const removedBTHomeControl = (removedKeys: string[]): boolean =>
	removedKeys.some((key) => key.startsWith(BTHOME_CONTROL_KEY_PREFIX));

export const setBluetoothRequest = async (device, enable, observerEnable) => {
	const settings = devices_settings.getByRealId(device.id);
	if (firmwareOmitsBleEnable(settings)) {
		return { isok: true } as SettingRequest;
	}

	const config = buildBleSetConfig(settings, {
		enable,
		observerEnable: observerEnable ? true : undefined,
	});

	const res: SettingRequest = await gen2Request(device, 'Ble.SetConfig', {
		config,
	});

	if (res?.isok) {
		if (observerEnable) {
			settings.ble.observer = { enable: observerEnable };
		}
		settings.ble.enable = enable;
		devices_settings.update_devices_settings(device.id, settings);
	}

	return res;
};
